import React, { useCallback, useState, Suspense, lazy } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { isTenantAdminForTenant } from '../../platform/membershipPermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import useBrowserBackStack from '../../hooks/useBrowserBackStack';
import { isPremiumVisualModule, PREMIUM_MODULE_IDS } from '../../platform/contentListVisual';
import './Sindikalist.css';

const SindikalistCreate = lazy(() => import('./SindikalistCreate'));
const SindikalistList = lazy(() => import('./SindikalistList'));
const SindikalistDetail = lazy(() => import('./SindikalistDetail'));

function Sindikalist() {
    const [selectedSindikalist, setSelectedSindikalist] = useState(null);
    const [showCreateSindikalist, setShowCreateSindikalist] = useState(false);

    const { user } = useUser();
    const { tenantId } = useTenant();
    const { memberships } = useMembership();
    const isAdmin = isTenantAdminForTenant(memberships, tenantId, user);

    const handleSelectSindikalist = (sindikalist) => {
        setSelectedSindikalist(sindikalist);
        setShowCreateSindikalist(false);
    };

    const resetToList = useCallback(() => {
        setSelectedSindikalist(null);
        setShowCreateSindikalist(false);
    }, []);

    const onList = !selectedSindikalist && !showCreateSindikalist;
    const closeDetailView = useBrowserBackStack(!onList, resetToList);

    const toggleCreateSindikalistForm = () => {
        if (showCreateSindikalist) {
            closeDetailView();
            return;
        }
        setShowCreateSindikalist(true);
        setSelectedSindikalist(null);
    };

    const handleSindikalistCreated = () => {
        setShowCreateSindikalist(false);
        setSelectedSindikalist(null);
    };

    const headerTitle = selectedSindikalist?.title
      || (showCreateSindikalist ? 'Novo izdanje' : 'Sindikalist');

    return (
        <div className="platform-module-container sindikalist-container">
                <ModulePageHeader
                    eyebrow={!onList ? 'Sindikalist' : undefined}
                    title={headerTitle}
                    subtitle={onList ? 'Izdanja sindikalnega glasila.' : undefined}
                    actions={(
                        <>
                            {!selectedSindikalist && isAdmin && (
                                <Button variant="primary" onClick={toggleCreateSindikalistForm}>
                                    {showCreateSindikalist
                                      ? 'Nazaj na izbiro'
                                      : (isPremiumVisualModule(PREMIUM_MODULE_IDS.SINDIKALIST) ? 'Novo izdanje' : 'Naloži dokument')}
                                </Button>
                            )}
                            {selectedSindikalist && (
                                <Button variant="primary" onClick={closeDetailView}>Nazaj na izbiro</Button>
                            )}
                        </>
                    )}
                />
                <Suspense fallback={(
                  <div className="platform-content-list-loading">
                    <Spinner animation="border" role="status" size="sm">
                      <span className="visually-hidden">Nalaganje...</span>
                    </Spinner>
                  </div>
                )}>
                    {!selectedSindikalist ? (
                        showCreateSindikalist ? (
                            <SindikalistCreate onSindikalistCreated={handleSindikalistCreated} />
                        ) : (
                            <SindikalistList onSelectSindikalist={handleSelectSindikalist} isAdmin={isAdmin} />
                        )
                    ) : (
                        <SindikalistDetail sindikalist={selectedSindikalist} />
                    )}
                </Suspense>
        </div>
    );
}

export default Sindikalist;
